import { useQuery, useQueryClient } from '@tanstack/react-query'
import { type FormEvent, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAddressAvailability } from '@/features/auth/hooks/use-address-availability'
import { type ChangeAddressReason, changeMailboxAddress, getMailboxInfo } from '@/features/auth/native-mailbox-api'
import { useToast } from '@/hooks/ui/use-toast'

const MAILBOX_INFO_KEY = ['native-mailbox-info'] as const
const LANGMAIL_DOMAIN = 'langmail.me'

const CHANGE_ERROR: Record<ChangeAddressReason, string> = {
  invalid: 'Use lowercase letters, numbers, dots, hyphens or underscores.',
  reserved: 'That address is reserved.',
  taken: 'That address was just taken.',
  unchanged: 'That is already your address.',
  cap_reached: 'You have used all your address changes.',
  not_provisioned: 'Your mailbox is not ready yet.',
  unavailable: 'Address changes are temporarily unavailable right now.',
}

// The settings "Langmail address" section: shows the current @langmail.me
// address, lets the user change it in place (reusing the signup availability
// check), and lists the previous addresses that still receive mail. A rename is
// capped, so the section disables itself once no changes remain.
export function LangmailAddress() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [editing, setEditing] = useState(false)
  const [username, setUsername] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { data: info, isLoading } = useQuery({ queryKey: MAILBOX_INFO_KEY, queryFn: getMailboxInfo })
  // Only run the live availability check while the editor is open.
  const availability = useAddressAvailability(editing ? username : '')

  if (isLoading || !info) {
    // No active native mailbox (or still loading) — nothing to show.
    return null
  }

  const canChange = info.renamesRemaining > 0
  const newAddress = `${username}@${LANGMAIL_DOMAIN}`
  const canSubmit = availability.status === 'available' && !submitting

  const closeEditor = () => {
    setEditing(false)
    setUsername('')
    setError(null)
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setError(null)
    setSubmitting(true)
    const result = await changeMailboxAddress(username)
    setSubmitting(false)
    if (result.ok) {
      toast({ title: 'Address changed', description: `Your address is now ${result.address}.` })
      closeEditor()
      await queryClient.invalidateQueries({ queryKey: MAILBOX_INFO_KEY })
      return
    }
    setError(CHANGE_ERROR[result.reason])
  }

  return (
    <div className="mb-8">
      <p className="mb-1 font-medium font-mono text-[11px] text-muted-foreground uppercase tracking-[1.5px]">
        Langmail address
      </p>
      <p className="mb-3 text-[13px] text-muted-foreground italic">The @langmail.me address that receives your mail.</p>

      <div className="flex items-center justify-between gap-3 rounded-xl border border-border bg-secondary p-4">
        <span className="font-mono text-[15px] text-foreground" data-testid="langmail-current-address">
          {info.address}
        </span>
        {canChange && !editing && (
          <Button onClick={() => setEditing(true)} type="button" variant="outline">
            Change address
          </Button>
        )}
      </div>

      <p className="mt-2 text-[13px] text-muted-foreground">
        {canChange
          ? `${info.renamesRemaining} address change${info.renamesRemaining === 1 ? '' : 's'} remaining.`
          : 'You have used all your address changes.'}
      </p>

      {editing ? (
        <form className="mt-4 space-y-3" onSubmit={handleSubmit}>
          <div className="space-y-1.5">
            <Label htmlFor="new-address">New address</Label>
            <div className="flex items-center gap-2">
              <Input
                autoCapitalize="none"
                autoComplete="off"
                autoCorrect="off"
                id="new-address"
                onChange={(event) => setUsername(event.target.value)}
                placeholder="alexandra"
                spellCheck={false}
                value={username}
              />
              <span className="font-mono text-muted-foreground text-sm">@{LANGMAIL_DOMAIN}</span>
            </div>
            <AddressHint state={availability} />
          </div>

          {info.loginWillChange && availability.status === 'available' && (
            <p className="rounded-lg bg-accent p-3 text-[13px] text-foreground">
              This address is also your login. After the change you'll sign in with{' '}
              <span className="font-mono font-semibold">{newAddress}</span>.
            </p>
          )}

          {error ? <p className="text-destructive text-sm">{error}</p> : null}

          <div className="flex items-center gap-2">
            <Button disabled={!canSubmit} type="submit">
              {submitting ? 'Changing…' : 'Confirm change'}
            </Button>
            <Button disabled={submitting} onClick={closeEditor} type="button" variant="ghost">
              Cancel
            </Button>
          </div>
        </form>
      ) : null}

      {info.previousAddresses.length > 0 && (
        <div className="mt-4" data-testid="langmail-previous-addresses">
          <p className="mb-1.5 font-medium text-muted-foreground text-xs">Previous addresses (still receive mail)</p>
          <ul className="space-y-1">
            {info.previousAddresses.map((address) => (
              <li className="font-mono text-[13px] text-muted-foreground" key={address}>
                {address}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

const AddressHint = ({ state }: { state: ReturnType<typeof useAddressAvailability> }) => {
  if (state.status === 'idle') {
    return null
  }
  if (state.status === 'checking') {
    return <p className="font-mono text-muted-foreground text-xs">Checking…</p>
  }
  if (state.status === 'available') {
    return <p className="font-mono text-[#4E8A76] text-xs dark:text-[#7FB0A0]">Available</p>
  }
  if (state.status === 'error') {
    return <p className="font-mono text-muted-foreground text-xs">Couldn’t check availability.</p>
  }
  return <p className="text-destructive text-xs">{CHANGE_ERROR[state.reason]}</p>
}
